import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/Badge";
import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import Icon from "@/components/Icon/Icon";
import HStack from "@/components/Layout/HStack";
import VStack from "@/components/Layout/VStack";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { CertificationKind } from "@/routes/_staff/certification/-constants";

import { Columns } from "../-constants";
import { CertificationsTableRow } from "../-query/type";

export type NameCell = {
  certificationName: string;
  certificationKind: CertificationKind;
};

export default function nameColumn<
  T extends CertificationsTableRow,
>(): ColumnDef<T> {
  const certificationKindToTranslationKey: Record<
    CertificationKind,
    "base" | "specialty" | "handling"
  > = {
    [CertificationKind.BASE]: "base",
    [CertificationKind.SPECIALTY]: "specialty",
    [CertificationKind.HANDLING]: "handling",
  };

  return {
    accessorKey: Columns.CERTIFICATION_NAME,
    enablePinning: true,
    meta: {
      i18nKey: "educationsAndQualifications.certificationName",
    },
    header: DataTableColumnHeader,
    cell({ row }) {
      const { t } = useTranslation(defaultNS, {
        keyPrefix: "pages.certification.certificationKind",
      });
      const data = row.original;
      return (
        <HStack className="gap-2 items-center text-nowrap">
          <VStack className="gap-1 p-2">
            {data.certificationKind === CertificationKind.BASE ? (
              <Text variant="labelSemiLarge" className="text-primary-fill py-2">
                {data.certificationName}
              </Text>
            ) : (
              <VStack
                className={cn("gap-1 items-start", row.depth > 0 ? "pl-4" : "")}
              >
                <Badge
                  variant={
                    data.certificationKind === CertificationKind.SPECIALTY
                      ? "primaryMuted"
                      : "complementaryMuted"
                  }
                  className="font-semibold"
                >
                  {t(certificationKindToTranslationKey[data.certificationKind])}
                </Badge>
                <Text variant="labelSemiLarge" className="text-primary-fill">
                  {data.certificationName}
                </Text>
              </VStack>
            )}
          </VStack>

          <div className="ml-auto">
            {row.getCanExpand() && (
              <Button
                variant="ghost"
                size="icon"
                onClick={row.getToggleExpandedHandler()}
                className="hover:bg-transparent"
              >
                {row.getIsExpanded() ? (
                  <Icon iconName="ChevronUpIcon" />
                ) : (
                  <Icon iconName="ChevronDownIcon" />
                )}
              </Button>
            )}
          </div>
        </HStack>
      );
    },
  } satisfies ColumnDef<T>;
}
